import { AccountBalanceWallet, Edit, EuroSymbol, Delete, Add } from "@mui/icons-material";
import { Button } from "@mui/material";
import { customerArButtonTitle, ArDialog } from "./ArDialog";
import { useDialog, useSimpleDialog, yesNoButtons } from "../../common/dialog";
import { PageTitle } from "../../layout/PageTitle";
import { createListStore } from "../../common/listStore";
import { listHeaderItems, renderListStore } from "../../common/listStoreUi";
import { CustomerStateId, useCustomerStates } from "../../common/enum";
import { useState } from "react";
import { UserTypeId, useAccountStore } from "../../common/account";
import { PaymentMatchDialog } from "./PaymentMatchDialog";
import { createAxios } from "../../common/axios";
import { useDefaultValues } from "../../common/defaultValues";
import { landCodes, useRxJsSub } from "../../common/utils";
import { CustomerListItemDto } from "./types";
import { asapScheduler, distinctUntilChanged, map, observeOn } from "rxjs";

export function CustomerListPage() {
    const accountStore = useAccountStore(p => ({ userTypeId: p.data?.accessTokenDecoded.userTypeId }));
    const [useCustomerStore] = useState(() => createListStore<CustomerListItemDto>({
        path: "customers/customers/list",
        getId: p => p.itemId,
        autoInitialFetch: false,
        supportsApiFilterSortPage: true,
        itemsPerPage: 15,
    }));
    useRxJsSub(() => useDefaultValues.getState().clientData$.pipe(map(p => p.data.landCode), distinctUntilChanged(), observeOn(asapScheduler)).subscribe({
        next(landCode) {
            useCustomerStore.getState().setListProps([
                { column: "custId", label: "Customer", width: 120, filter: true, transformFilterValue: p => typeof p === "string" ? p.trim().substring(0, 7) : p },
                { column: "fname", label: "First name", width: 120, filter: true },
                { column: "lname", label: "Last name", width: 150, filter: true },
                { column: "strt", label: "Address", width: 220 },
                { label: "Street", filter: ["strt1"] },
                { label: "House nr.", filter: ["strt2"] },
                { column: "zipc", label: "ZIP code", width: 100, filter: true },
                { column: "city", label: "City", width: 200, filter: true },
                { label: "Phone", filter: ["tel", "mob"] },
                { column: "tel", label: "Tel", width: 125 },
                { column: "mob", label: "Mobile", width: 125 },
                { column: "email", label: "Email", width: 220, filter: true },
                {
                    column: "pstNr", label: "Post number", filter: true, operator: "Equal", width:
                        landCodes("EE", "LV").includes(landCode) ? 170 :
                            landCodes("HU").includes(landCode) ? 225 :
                                130
                },
                {
                    column: "customerStateId", label: "Status", width: 150, sortable: false, filter: true, enumStore: useCustomerStates,
                    readOnlyValue: accountStore => accountStore.userTypeId !== UserTypeId.CollectionAgency ? null : CustomerStateId.BadPayer,
                },
            ]);
        },
    }));
    const customers = useCustomerStore(p => ({
        selectedItem: p.selectedItem,
    }));
    const dialogAr = useDialog(close => <ArDialog close={close} customer={customers.selectedItem!} />);
    const paymentMatchDialog = useDialog(close => <PaymentMatchDialog close={close} customer={customers.selectedItem!} />, "xl");
    const canAddNewCustomer = accountStore.userTypeId !== UserTypeId.View && accountStore.userTypeId !== UserTypeId.CollectionAgency;
    const simpleDialog = useSimpleDialog();
    async function deleteCustomer() {
        await simpleDialog.show("Permanently delete customer", "Permanently delete selected customer?", undefined, yesNoButtons(async () => {
            try {
                await createAxios().delete(`customers/customers/${customers?.selectedItem?.id}`);
            }
            finally {
                useCustomerStore.getState().fetch();
            }
        }));
    }

    function addNewCustomer() {
        window.open("/customers/new");
    }

    function openCustomerDetail(customer: CustomerListItemDto) {
        window.open(`/customers/${customer.custId}`, "_blank");
    }

    return <>
        {dialogAr.render()}
        {paymentMatchDialog.render()}
        {simpleDialog.render()}
        <PageTitle title="Customers" />
        {renderListStore(useCustomerStore, {
            header: listHeaderItems([
                canAddNewCustomer && ["add", () => <Button color="add" title="Add" onClick={addNewCustomer}><Add /></Button>],
                ["edit", () => <Button color="edit" disabled={!customers.selectedItem} title="Edit" onClick={() => openCustomerDetail(customers.selectedItem!)}><Edit /></Button>],
                accountStore.userTypeId === UserTypeId.Admin && ["ar", () => <Button title={customerArButtonTitle}
                    disabled={!customers.selectedItem} onClick={() => dialogAr.show()}><AccountBalanceWallet /></Button>],
                accountStore.userTypeId === UserTypeId.Admin && ["paymentMatch", () => <Button title="Match payments"
                    disabled={!customers.selectedItem} onClick={() => paymentMatchDialog.show()}><EuroSymbol /></Button>],
                accountStore.userTypeId === UserTypeId.Admin && ["remove", () => <Button color="delete" disabled={customers.selectedItem == null} title="Permanently delete" onClick={deleteCustomer}><Delete /></Button>],
            ]),
            onDoubleClick: openCustomerDetail,
            getRowClass: p => (p.isDeleted && ["redRibbon", "strikeThrough"]),
        })}
    </>;
}
